import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ScrollProgressBar } from './ScrollProgressBar';
import { Link } from 'react-router-dom';

interface LandingLayoutProps {
  children: React.ReactNode;
}

export const LandingLayout = ({ children }: LandingLayoutProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <ScrollProgressBar />
      
      {/* Header */}
      <header className="border-b" role="banner">
        <nav className="container mx-auto flex items-center justify-between px-4 py-4" role="navigation" aria-label="Main navigation">
          <Link to="/" className="flex items-center gap-2" itemScope itemType="https://schema.org/Organization">
            <Building2 className="h-6 w-6 text-primary" aria-hidden="true" />
            <span className="text-xl font-bold" itemProp="name">Rental Manager</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" onClick={() => navigate('/auth')} aria-label="Sign in to your account">
              {t('auth.signIn')}
            </Button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main role="main">{children}</main>

      {/* Footer */}
      <footer className="bg-muted py-12" role="contentinfo" itemScope itemType="https://schema.org/WPFooter">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <Building2 className="h-6 w-6 text-primary" aria-hidden="true" />
                <span className="text-xl font-bold">Rental Manager</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {t('landing.footer.description')}
              </p>
            </div>
            <nav aria-label="Product navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.product')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/#pricing" className="hover:text-foreground transition-colors">
                    {t('landing.footer.pricing')}
                  </Link>
                </li>
                <li>
                  <button onClick={() => navigate('/auth')} className="hover:text-foreground transition-colors">
                    {t('landing.footer.getStarted')}
                  </button>
                </li>
                <li>
                  <span className="text-muted-foreground/50">{t('landing.footer.roadmap')}</span>
                </li>
              </ul>
            </nav>
            <nav aria-label="Resources navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.resources')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/airbnb-cleaning-schedule-software" className="hover:text-foreground transition-colors">
                    Cleaning Schedule Software
                  </Link>
                </li>
                <li>
                  <Link to="/stop-airbnb-double-bookings" className="hover:text-foreground transition-colors">
                    Stop Double Bookings
                  </Link>
                </li>
                <li>
                  <Link to="/airbnb-turnover-checklist-free-download" className="hover:text-foreground transition-colors">
                    Free Turnover Checklist
                  </Link>
                </li>
                <li>
                  <span className="text-muted-foreground/50">{t('landing.footer.blog')}</span>
                </li>
              </ul>
            </nav>
            <nav aria-label="Legal navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <button 
                    onClick={() => navigate('/privacy')} 
                    className="hover:text-foreground transition-colors"
                  >
                    {t('landing.footer.privacy')}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/terms')} 
                    className="hover:text-foreground transition-colors"
                  >
                    {t('landing.footer.terms')}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
          <div className="mt-8 border-t pt-8 text-center text-sm text-muted-foreground">
            {t('landing.footer.copyright')}
            <div className="mt-4 text-xs text-muted-foreground/70 space-y-2">
              <p>🌍 Available in EU, US, UK, and 50+ countries</p>
              <p>🇪🇺 EU-hosted in Frankfurt • GDPR compliant</p>
              <p>Support: support@rental.shift-box.com</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
